/**
 * Course email templates that supply the post-section student email's copy.
 *
 * A template under `email-templates-per-course/{courseId}/docs` takes part
 * when it carries a {@link POST_SECTION_TEMPLATE_FIELD} map. Its `kind` says
 * which email it is copy for: the weekly progress email (the default) or the
 * short missed-section nudge. Without a `courseWeek` it is that kind's course
 * default, and its `enabled` flag is the course's switch for that email. With
 * a `courseWeek`, a weekly template overrides the copy for that week; the
 * nudge has no week overrides. The template editor keeps the map, since it
 * saves only the fields it edits with `merge: true`.
 */
import {z} from 'zod';

/** The template field that makes a course email template post-section copy. */
export const POST_SECTION_TEMPLATE_FIELD = 'postSection';

/** The emails a post-section template can be copy for. */
export const POST_SECTION_TEMPLATE_KINDS = ['weekly', 'missedNudge'] as const;

/** One of {@link POST_SECTION_TEMPLATE_KINDS}. */
export type PostSectionTemplateKind =
  (typeof POST_SECTION_TEMPLATE_KINDS)[number];

/**
 * A Firestore timestamp as stored, read by its seconds and nanoseconds. Both
 * SDKs' `Timestamp` classes expose these, so a client-side read and an Admin
 * SDK read parse alike.
 */
const StoredTimestampSchema = z.object({
  seconds: z.number(),
  nanoseconds: z.number(),
});

/** Schema of a template's {@link POST_SECTION_TEMPLATE_FIELD} map. */
export const PostSectionTemplateSettingsSchema = z.object({
  /**
   * On the course default, whether the course sends the email at all. On a
   * week override, whether the override is used.
   */
  enabled: z.boolean(),
  /** The 1-based course week the copy is for. Absent: the course default. */
  courseWeek: z.number().int().min(1).optional(),
  /** Which email the template is copy for. Absent: `weekly`. */
  kind: z.enum(POST_SECTION_TEMPLATE_KINDS).optional(),
  /**
   * On the nudge's course default, when its switch last went from off to on.
   * The nudge covers only sections that end after this moment, so slots that
   * ended before it, possibly emailed by hand, are never nudged. An enabled
   * nudge default without it sends nothing.
   */
  enabledAt: StoredTimestampSchema.optional(),
});

/** A template's {@link POST_SECTION_TEMPLATE_FIELD} map. */
export type PostSectionTemplateSettings = z.infer<
  typeof PostSectionTemplateSettingsSchema
>;

/**
 * Which email a template's settings make it copy for.
 *
 * @param settings - The template's post-section settings.
 * @return The `kind`, or `weekly` when it is absent.
 * @throws Never.
 */
export function getPostSectionTemplateKind(
  settings: Pick<PostSectionTemplateSettings, 'kind'>,
): PostSectionTemplateKind {
  return settings.kind ?? 'weekly';
}

/**
 * When a nudge default's switch was ticked, as epoch milliseconds.
 *
 * @param settings - The template's post-section settings.
 * @return The instant, or undefined when `enabledAt` is absent.
 * @throws Never.
 */
export function getPostSectionEnabledAtMs(
  settings: Pick<PostSectionTemplateSettings, 'enabledAt'>,
): number | undefined {
  const {enabledAt} = settings;
  if (enabledAt === undefined) {
    return undefined;
  }
  return enabledAt.seconds * 1000 + Math.floor(enabledAt.nanoseconds / 1e6);
}

const PostSectionTemplateDocumentSchema = z.object({
  subject: z.string().trim().catch(''),
  html: z.string().trim().catch(''),
  archived: z.boolean().optional(),
  [POST_SECTION_TEMPLATE_FIELD]: PostSectionTemplateSettingsSchema,
});

/** A course email template that supplies post-section copy. */
export interface PostSectionTemplate {
  /** The template's id within the course's templates. */
  templateId: string;
  /** Handlebars subject. Empty means the next template down supplies it. */
  subject: string;
  /** Handlebars HTML body. Empty means the next template down supplies it. */
  html: string;
  /** Which copy the template is, and whether it is on. */
  settings: PostSectionTemplateSettings;
}

/** Outcome of reading one template as post-section copy. */
export type ParsedPostSectionTemplate =
  | {status: 'active'; template: PostSectionTemplate}
  | {status: 'archived'}
  | {status: 'invalid'; issues: string};

/**
 * Reads one course email template as post-section copy.
 *
 * @param params.templateId - The template's document id.
 * @param params.data - The template document's data.
 * @return `active` with the template; `archived` for an archived template,
 *   which takes no part; or `invalid` with the validation issues when the
 *   {@link POST_SECTION_TEMPLATE_FIELD} map is missing or malformed.
 * @throws Never.
 */
export function parsePostSectionTemplate({
  templateId,
  data,
}: {
  templateId: string;
  data: unknown;
}): ParsedPostSectionTemplate {
  const parsed = PostSectionTemplateDocumentSchema.safeParse(data);
  if (!parsed.success) {
    return {status: 'invalid', issues: z.prettifyError(parsed.error)};
  }
  if (parsed.data.archived === true) {
    return {status: 'archived'};
  }
  return {
    status: 'active',
    template: {
      templateId,
      subject: parsed.data.subject,
      html: parsed.data.html,
      settings: parsed.data[POST_SECTION_TEMPLATE_FIELD],
    },
  };
}
